import { AxiosResponse } from 'axios';
import i18n from '@/i18n';

const CATEGORIES_URL = '/property-api/promotions-categories';

/**
 * Translates promotion category fields that come from the BE using i18n keys.
 * Applies to: title, description, example, promotionsFields[].name, rules[].error_message.
 *
 * Translation keys follow the pattern:
 *   promotions.categories.<id>.title
 *   promotions.categories.<id>.description
 *   promotions.categories.<id>.example
 *   promotions.categories.<id>.fields.<key>.name
 *   promotions.categories.<id>.rules.<index>.errorMessage
 *
 * Falls back to the original BE value if no translation key exists.
 */
export const promotionsCategoriesTranslationMiddleware = (response: AxiosResponse): AxiosResponse => {
  const isGetRequest = response.config.method?.toUpperCase() === 'GET';
  const isCategoriesEndpoint = response.config.url?.includes(CATEGORIES_URL);

  if (i18n.locale === 'en' || !isGetRequest || !isCategoriesEndpoint) {
    return response;
  }

  const t = (key: string, fallback: string): string => (i18n.te(key) ? i18n.t(key) as string : fallback);

  const data: any[] = Array.isArray(response.data?.data) ? response.data.data : [];

  response.data.data = data.map((category: any) => {
    const base = `promotions.categories.${category.id}`;

    return {
      ...category,
      title: t(`${base}.title`, category.title),
      description: t(`${base}.description`, category.description),
      example: t(`${base}.example`, category.example),
      promotionsFields: (category.promotionsFields ?? []).map((field: any) => ({
        ...field,
        name: t(`${base}.fields.${field.key}.name`, field.name),
      })),
      rules: (category.rules ?? []).map((rule: any, index: number) => ({
        ...rule,
        error_message: t(`${base}.rules.${index}.errorMessage`, rule.error_message),
      })),
    };
  });

  return response;
};
